import React, { useContext, useState, useEffect } from "react";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, company, emailaddress } from "../controller/global";
import { LuArrowUpRight, LuX, LuMail, LuPhone, LuCheck } from "react-icons/lu";
import logo from "../assets/images/inqmise-wordmark.png";
import { submitLead } from "../controller/leads";
import useToast from "./useToast";
import useDialog from "./useDialog";

const Contactus = () => {
    const { isOpen, setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [messageApi, contextHolder] = useToast();
    const dialogRef = useDialog(isOpen, () => handleClose());

    // Lock background scroll while the modal is open so only the modal's
    // own content scrolls, not the page behind it.
    useEffect(() => {
        if (isOpen) {
            const previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = previousOverflow; };
        }
    }, [isOpen]);

    // Validate function
    const validateField = (name, value) => {
        let error = "";
        switch (name) {
            case "fullname":
                if (!value.trim()) error = "Full name is required.";
                break;
            case "phone":
                if (value.trim() && !isValidPhone(value)) error = "Enter a valid phone number.";
                break;
            case "email":
                if (!value.trim()) error = "Email is required.";
                else if (!emailRegex.test(value.toLowerCase())) error = "Enter a valid email address.";
                break;
            case "subject":
                if (!value.trim()) error = "Subject is required.";
                break;
            case "description":
                if (!value.trim()) error = "Tell us a little about your project.";
                break;
            default:
                break;
        }
        return error;
    };

    // Handle change in input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        const error = validateField(name, value);
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const success = () => {
        messageApi.open({
          type: 'success',
          content: 'Thank you. Your project inquiry has been received.',
        });
      };
    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitting) return;
        const newErrors = {
            fullname: validateField("fullname", formData.fullname),
            phone: validateField("phone", formData.phone),
            email: validateField("email", formData.email),
            subject: validateField("subject", formData.subject),
            description: validateField("description", formData.description),
        };
        if (Object.values(newErrors).some((error) => error)) {
            setErrors(newErrors);
            const firstInvalid = Object.keys(newErrors).find((name) => newErrors[name]);
            document.getElementById(`contactus-${firstInvalid}`)?.focus();
            return;
        }

        setSubmitting(true);
        submitLead(formData, "modal")
            .then(() => {
                success();
                setIsOpen(false);
                setFormData({
                    fullname: "",
                    phone: "",
                    email: "",
                    subject: "",
                    description: "",
                });
                setErrors({});
            })
            .catch(() => {
                // Previously this only wrote to the console: the visitor saw
                // nothing at all and had no idea their enquiry had not sent.
                messageApi.open({
                    type: "error",
                    content: "Sorry — that did not send. Please try again, or email us directly.",
                });
            })
            .finally(() => setSubmitting(false));
    };

    // Handle modal close
    const handleClose = () => {
        setFormData({
            fullname: "",
            phone: "",
            email: "",
            subject: "",
            description: "",
        });
        setErrors({});
        setIsOpen(false);
    };

    const AUTOCOMPLETE = { fullname: "name", phone: "tel", email: "email" };
    const PLACEHOLDERS = { fullname: "Your full name", email: "you@company.com", phone: "+91", subject: "What would you like to build?" };
    const field = (label, name, type = "text") => (
        <div className="cq-field">
            <label htmlFor={`contactus-${name}`}>{label}{name === "phone" ? <span>Optional</span> : <span aria-hidden="true">*</span>}</label>
            <input id={`contactus-${name}`} type={type} name={name} autoComplete={AUTOCOMPLETE[name] || "off"}
                placeholder={PLACEHOLDERS[name]} required={name !== "phone"} value={formData[name]} onChange={handleChange}
                aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `contactus-${name}-error` : undefined} />
            {errors[name] && <p id={`contactus-${name}-error`} className="cq-field-error">{errors[name]}</p>}
        </div>
    );

    return (
        <>
            {contextHolder}
            {isOpen && (
                <div className="cq-overlay">
                    <div className="cq-backdrop" aria-hidden="true" onClick={handleClose} />
                    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="contactus-title" aria-describedby="contactus-intro" tabIndex={-1} className="cq-dialog">
                        <button type="button" onClick={handleClose} aria-label="Close contact form" className="cq-close"><LuX aria-hidden="true" /></button>
                        <aside className="cq-intro">
                            <img className="cq-brand-logo" src={logo} alt="InQmise Technologies" width="576" height="134" />
                            <div className="cq-intro-copy">
                                <span>Let’s start a conversation</span>
                                <h2>Your next idea.<br />A clear next step.</h2>
                                <p>Tell us your goals, what you need and where you are in the process. We’ll help you plan the way forward.</p>
                                <ul><li><LuCheck aria-hidden="true" />Software, web and mobile apps</li><li><LuCheck aria-hidden="true" />AI integration and automation</li><li><LuCheck aria-hidden="true" />Cloud and ongoing support</li></ul>
                            </div>
                            <div className="cq-direct">
                                <span>Prefer to reach us directly?</span>
                                <a href={`mailto:${emailaddress}`}><LuMail aria-hidden="true" /><span>{emailaddress}</span></a>
                                <a href={`tel:${company.phoneHref}`}><LuPhone aria-hidden="true" /><span>{company.phone}</span></a>
                            </div>
                        </aside>
                        <div className="cq-content">
                            <div className="cq-heading">
                                <h3 id="contactus-title">Contact us</h3>
                                <p id="contactus-intro">Share a few details about your project.<br />We aim to reply within one business day.</p>
                            </div>
                            <form onSubmit={handleSubmit} className="cq-form" noValidate>
                                <div className="cq-field-row">{field("Full name", "fullname")}{field("Email", "email", "email")}</div>
                                {field("Phone number", "phone", "tel")}
                                {field("Subject", "subject")}
                                <div className="cq-field">
                                    <label htmlFor="contactus-description">Project details<span aria-hidden="true">*</span></label>
                                    <textarea id="contactus-description" name="description" rows={4} placeholder="Describe your idea, current challenges or project requirements…" required value={formData.description} onChange={handleChange}
                                        aria-invalid={Boolean(errors.description)} aria-describedby={errors.description ? "contactus-description-error" : undefined} />
                                    {errors.description && <p id="contactus-description-error" className="cq-field-error">{errors.description}</p>}
                                </div>
                                <div className="cq-actions">
                                    <button type="submit" className="cq-submit" disabled={submitting}>{submitting ? "Sending…" : "Send project inquiry"}<LuArrowUpRight aria-hidden="true" /></button>
                                    <button type="button" onClick={handleClose} className="cq-cancel">Cancel</button>
                                </div>
                                <p className="cq-form-note">Your details help our team respond to your inquiry. Fields marked * are required.</p>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Contactus;
